import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { ASSETS } from "../data/entrepminds";

const AdminLogin = () => {
  const { login, status, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (status === "authed" && user) navigate("/admin", { replace: true });
  }, [status, user, navigate]);

  if (status === "authed" && user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Email and password required.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Login failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf7ee] text-[#0a0a0a] flex flex-col" data-testid="admin-login-page">
      <header className="px-6 md:px-10 py-6">
        <a href="/" className="inline-flex items-center gap-3" data-testid="admin-login-home-link">
          <img src={ASSETS.logo} alt="Entrepminds" className="h-10 w-auto" />
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          onSubmit={submit}
          data-testid="admin-login-form"
          className="w-full max-w-md em-card p-8 md:p-10"
        >
          <div className="font-mono-em text-xs tracking-[0.25em] uppercase text-[#8a6a10] mb-3">
            Secure Area
          </div>
          <h1 className="font-display text-3xl mb-2">Admin Login</h1>
          <p className="text-sm text-[#6b6356] mb-8">
            Sign in to review and manage GrowthOS™ inquiries.
          </p>

          <label className="font-mono-em text-[10px] tracking-[0.25em] uppercase text-[#6b6356]">
            Email
          </label>
          <div className="flex items-center gap-3 border-b border-[#e8e2cf] focus-within:border-[#d4af37] transition-colors">
            <Mail size={16} className="text-[#6b6356]" />
            <input
              data-testid="admin-login-email"
              type="email"
              autoComplete="email"
              className="em-input border-b-0"
              placeholder="admin@entrepmindsconsultancy.ai"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <label className="font-mono-em text-[10px] tracking-[0.25em] uppercase text-[#6b6356] mt-6 block">
            Password
          </label>
          <div className="flex items-center gap-3 border-b border-[#e8e2cf] focus-within:border-[#d4af37] transition-colors">
            <Lock size={16} className="text-[#6b6356]" />
            <input
              data-testid="admin-login-password"
              type="password"
              autoComplete="current-password"
              className="em-input border-b-0"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {error && (
            <div
              data-testid="admin-login-error"
              className="mt-5 text-sm text-red-400 border-l-2 border-red-400 pl-3"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="btn-gold mt-8 w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Signing in…
              </>
            ) : (
              <>
                Sign in <ArrowRight size={18} />
              </>
            )}
          </button>
        </motion.form>
      </main>
    </div>
  );
};

export default AdminLogin;
